import * as uuid from 'uuid';

import {Selector} from 'notebook/ELNConstants';
import {
  EvaluationOptions,
  TableEvaluation,
  stubbableFunctions,
} from 'notebook/mediocre/DocumentEvaluationUpdater';
import excess from 'notebook/tables/excess';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import * as Perf from 'performance/Perf';
import {
  OPERATION_EVALUATION_COMPARISON_FINISHED_EVENT,
  OPERATION_EVALUATION_COMPARISON_STARTED_EVENT,
  OPERATION_EVALUATION_FINISHED_EVENT,
  OPERATION_EVALUATION_STARTED_EVENT,
  TRADITIONAL_EVALUATION_FINISHED_EVENT,
  TRADITIONAL_EVALUATION_STARTED_EVENT,
} from 'performance/processNotebookEvents';

import {NoteRuntimeSnapshot, QueryResolution} from '../TableStoreTransforms';
import {
  FullTableMap,
  OutputTableMap,
  TableMappedDiffCoordinates,
  cacheIsPopulated,
  hash,
  revhash,
} from './Evaluator';
import Resolver from './Resolver';
import {compareAndLogEvaluationSnapshots} from './util/compareShadowEvaluation';

const OPERATIONAL_EVALUATION_SHADOW_COMPARISON_CELL_LIMIT = 10000;
const EVALUATION_PERF_INTERVAL = 60 * 1000;

function snapshotCellSize(snapshot: OutputTableMap): number {
  return Object.values(snapshot).reduce((acc, table) => acc + table.length * (table[0]?.length ?? 0), 0);
}

/*
 * If we're populating the cache via main evaluation but are trying shadow evaluation,
 * that means we're skipping the first round of shadow evaluation...
 * however because of a ?potential? bug/interesting behavior when running the first eval
 * after loading form a note runtime snapsnot, evaluations are triggered on some cells which were not changed by the edit
 * ... So if we don't update the coordinates of cells
 * which have resolutions, on the subsequent resolution evaluation we wont update those locations
 * when running operation evaluation! Because this is loaded from a note run time snapshot the values are already there
 * and are the same, but if we dont correct for this shadow evaluation comparison throws errors!
 */
const updateTableMappedDiffCoordinates = (
  resolver: Resolver,
  shouldPopulateCache: boolean,
  syncResolutions: Array<QueryResolution>,
  asyncResolutions: Array<Promise<QueryResolution>>,
  tableMappedDiffCoordinates?: TableMappedDiffCoordinates | null
): TableMappedDiffCoordinates | undefined | null => {
  if (shouldPopulateCache && (syncResolutions.length > 0 || asyncResolutions.length > 0)) {
    const newTableMappedDiffCoordinates = tableMappedDiffCoordinates || {};
    // construct a quick lookup of the diff coordinates with table name
    const DiffCoordinatesByHash = Object.keys(newTableMappedDiffCoordinates).reduce<Record<string, boolean>>(
      (acc, tableName) => {
        const tableCoordinates = newTableMappedDiffCoordinates[tableName];
        tableCoordinates.forEach((coordinate) => {
          const hashString = hash({
            table: tableName,
            row: coordinate.rowIndex,
            column: coordinate.columnIndex,
          });
          acc[hashString] = true;
        });
        return acc;
      },
      {}
    );

    // Only add a coordinate to the diff coordinates if it's not already there
    Object.keys(resolver.cellResolutionsMap).forEach((hashString) => {
      if (DiffCoordinatesByHash[hashString] !== true) {
        const {row, column, table} = revhash(hashString);
        const coordinate = {rowIndex: row, columnIndex: column};
        if (newTableMappedDiffCoordinates[table] == null) {
          newTableMappedDiffCoordinates[table] = [coordinate];
        } else {
          newTableMappedDiffCoordinates[table].push(coordinate);
        }
      }
    });
    return newTableMappedDiffCoordinates;
  } else {
    return tableMappedDiffCoordinates;
  }
};

/*
 * Evaluate every cell in the tableMap every time you run evaluation
 */
export const runTraditionalEvalution = ({
  itemCid,
  fileId,
  fullTableMap,
  selectors,
  noteRuntimeSnapshot,
  tableNameToProvenanceUuid,
}: {
  itemCid: string;
  fileId: string;
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  tableNameToProvenanceUuid: Record<PropertyKey, string | null>;
}): TableEvaluation => {
  if (window.debug) {
    // eslint-disable-next-line no-console
    console.info(`[EXCESS] [${fileId}] Running traditional evaluator for persistence`);
  }

  const resolver: Resolver = stubbableFunctions.makeResolver(noteRuntimeSnapshot, () => {
    const currentTableName = evaluator.env.currentTable!;
    return (
      tableNameToProvenanceUuid[currentTableName] ??
      // TODO(BNCH-19669): remove this fallback once provenanceUuid is backfilled for legacy results tables
      currentTableName
    );
  });

  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: excess.parse,
    resolver,
    fileId,
  });
  const evaluationSnapshot = evaluator.output();
  const {syncResolutions, asyncResolutions} = resolver.getResolutions();
  return {cid: itemCid, evaluationSnapshot, syncResolutions, asyncResolutions};
};

/*
 * Run Traditional Evaluation but also attempt to run Shadow Evaluation, assuming it default
 * to running traditional Evaluation
 */
export const attemptShadowEvaluationComparison = ({
  itemCid,
  fileId,
  fullTableMap,
  selectors,
  noteRuntimeSnapshot,
  tableNameToProvenanceUuid,
  opts,
}: {
  itemCid: string;
  fileId: string;
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  tableNameToProvenanceUuid: Record<PropertyKey, string | null>;
  opts?: EvaluationOptions;
}): TableEvaluation => {
  if (window.debug) {
    // eslint-disable-next-line no-console
    console.info(
      `[EXCESS] [${fileId}] Running traditional evaluator with shadow operational evaluation for persistence`
    );
  }

  logProfilerTimingEvent(TRADITIONAL_EVALUATION_STARTED_EVENT);

  const resolver: Resolver = stubbableFunctions.makeResolver(noteRuntimeSnapshot, () => {
    const currentTableName = evaluator.env.currentTable!;
    return (
      tableNameToProvenanceUuid[currentTableName] ??
      // TODO(BNCH-19669): remove this fallback once provenanceUuid is backfilled for legacy results tables
      currentTableName
    );
  });
  const tableMappedDiffCoordinates = opts?.tableMappedDiffCoordinates;

  const oldClientCacheId = noteRuntimeSnapshot.clientEvaluationId;
  const newClientCacheId = uuid.v4();

  const cachePopulated = cacheIsPopulated(fileId, oldClientCacheId);

  // To avoid running two full evaluation cycles, one for regular and one for the first,
  // non-cached Shadow Operational Evaluation Cycle, we ask the normal eval cycle to add
  // it's results to the cache for the subsequent Shadow Operational Evaluation Cycles
  const shouldPopulateCache = !cachePopulated;

  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: excess.parse,
    resolver,
    fileId,
    tableMappedDiffCoordinates,
    cacheVersionId: newClientCacheId,
    oldCacheVersionId: oldClientCacheId,
    shouldUseOperationalEvaluation: false,
    shouldPopulateCache,
  });

  // Aright assuming we're attempting shadow evaluation, we only want to actually do it if:
  // 1. We have a populated cache
  // 2. We have diff coordinates
  const shouldUseOperationalEvaulationComparison = cachePopulated && tableMappedDiffCoordinates;

  const evaluationSnapshot = evaluator.output({
    oldEvaluationSnapshot: noteRuntimeSnapshot.evaluationSnapshot,
    tableMappedDiffCoordinates,
    shouldPopulateCache,
  });

  const {syncResolutions, asyncResolutions} = resolver.getResolutions();
  logProfilerTimingEvent(TRADITIONAL_EVALUATION_FINISHED_EVENT);

  // AND ONE LAST CHECK
  // If we're attempting to do shadow evaluation, we should only do it if the evaluation snapshot is small enough
  // and we expect a small number of resolutions
  if (
    shouldUseOperationalEvaulationComparison &&
    snapshotCellSize(evaluationSnapshot) <= OPERATIONAL_EVALUATION_SHADOW_COMPARISON_CELL_LIMIT
  ) {
    const shadowEvaluatorPerfId =
      Perf.start(
        `shadow_entry_evaluation`,
        {enabled: true, getMetadata: () => ({})},
        {
          interval: EVALUATION_PERF_INTERVAL,
          aggregations: [Perf.Aggregation.COUNT, Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
        }
      ) ?? '';
    Perf.markStart(shadowEvaluatorPerfId);
    logProfilerTimingEvent(OPERATION_EVALUATION_STARTED_EVENT);
    logProfilerTimingEvent(OPERATION_EVALUATION_COMPARISON_STARTED_EVENT);

    const operationalResolver: Resolver = stubbableFunctions.makeResolver(noteRuntimeSnapshot, () => {
      const currentTableName = shadowOperationalEvaluator.env.currentTable!;
      return tableNameToProvenanceUuid[currentTableName] ?? currentTableName;
    });
    const shadowOperationalEvaluator = new excess.SpreadsheetEvaluator({
      fullTableMap,
      selectors,
      parse: excess.parse,
      resolver: operationalResolver,
      fileId,
      tableMappedDiffCoordinates,
      cacheVersionId: newClientCacheId,
      oldCacheVersionId: oldClientCacheId,
      shouldPopulateCache: true,
      shouldUseOperationalEvaluation: true,
    });
    const operationalEvaluationSnapshot = shadowOperationalEvaluator.output({
      oldEvaluationSnapshot: noteRuntimeSnapshot.evaluationSnapshot,
      tableMappedDiffCoordinates,
      shouldPopulateCache: true,
    });
    logProfilerTimingEvent(OPERATION_EVALUATION_FINISHED_EVENT);

    compareAndLogEvaluationSnapshots(
      evaluationSnapshot,
      operationalEvaluationSnapshot,
      tableMappedDiffCoordinates,
      fileId,
      oldClientCacheId
    );
    Perf.markEnd(shadowEvaluatorPerfId);
    logProfilerTimingEvent(OPERATION_EVALUATION_COMPARISON_FINISHED_EVENT);
  }

  const updatedTableMappedDiffCoordinates = updateTableMappedDiffCoordinates(
    resolver,
    shouldPopulateCache,
    syncResolutions,
    asyncResolutions,
    tableMappedDiffCoordinates
  );

  return {
    cid: itemCid,
    evaluationSnapshot,
    syncResolutions,
    asyncResolutions,
    tableMappedDiffCoordinates: updatedTableMappedDiffCoordinates,
    clientCacheId: newClientCacheId,
  };
};

/*
 * Run Operational Evaluation, where a diff of what cells changed is passed in
 * and only those cells are evaluated, assuming they are not dependencies for other cells
 * and new rows/columns aren't being added/deleted
 */
export const runOperationEvaluation = ({
  itemCid,
  fileId,
  fullTableMap,
  selectors,
  noteRuntimeSnapshot,
  tableNameToProvenanceUuid,
  opts,
}: {
  itemCid: string;
  fileId: string;
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  tableNameToProvenanceUuid: Record<PropertyKey, string | null>;
  opts?: EvaluationOptions;
}): TableEvaluation => {
  if (window.debug) {
    // eslint-disable-next-line no-console
    console.info(`[EXCESS] [${fileId}] Running operational evaluator for persistence`);
  }

  const resolver: Resolver = stubbableFunctions.makeResolver(noteRuntimeSnapshot, () => {
    const currentTableName = evaluator.env.currentTable!;
    return (
      tableNameToProvenanceUuid[currentTableName] ??
      // TODO(BNCH-19669): remove this fallback once provenanceUuid is backfilled for legacy results tables
      currentTableName
    );
  });
  const tableMappedDiffCoordinates = opts?.tableMappedDiffCoordinates;

  const oldClientCacheId = noteRuntimeSnapshot.clientEvaluationId;
  const newClientCacheId = uuid.v4();
  const cachePopulated = cacheIsPopulated(fileId, oldClientCacheId);

  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: excess.parse,
    resolver,
    fileId,
    tableMappedDiffCoordinates,
    cacheVersionId: newClientCacheId,
    oldCacheVersionId: oldClientCacheId,
    shouldUseOperationalEvaluation: true,
    shouldPopulateCache: true,
  });

  const evaluationSnapshot = evaluator.output({
    oldEvaluationSnapshot: noteRuntimeSnapshot.evaluationSnapshot,
    tableMappedDiffCoordinates,
    shouldPopulateCache: true,
  });

  const {syncResolutions, asyncResolutions} = resolver.getResolutions();

  const updatedTableMappedDiffCoordinates = updateTableMappedDiffCoordinates(
    resolver,
    !cachePopulated,
    syncResolutions,
    asyncResolutions,
    tableMappedDiffCoordinates
  );

  return {
    cid: itemCid,
    evaluationSnapshot,
    syncResolutions,
    asyncResolutions,
    tableMappedDiffCoordinates: updatedTableMappedDiffCoordinates,
    clientCacheId: newClientCacheId,
  };
};
